import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../assets/logo.png';
import { useLanguage } from '../context/LanguageContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  const links = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.services'), path: '/services' },
    { name: t('nav.doctors'), path: '/doctors' },
    { name: t('nav.branches'), path: '/branches' },
    { name: t('nav.gallery'), path: '/gallery' },
    { name: t('nav.contact'), path: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md py-2' : 'bg-white/95 py-3.5'}`}>
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center py-1 group">
            <img 
              src={logo} 
              alt="Sree Tekke Eye Clinic" 
              className="h-9 sm:h-10 md:h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.02]" 
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-6 xl:space-x-7">
            <div className="flex space-x-5 xl:space-x-6">
              {links.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors hover:text-clinical ${
                    location.pathname === link.path ? 'text-clinical font-semibold' : 'text-navy'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            <Link
              to="/appointment"
              className="bg-clinical hover:bg-navy text-white px-5 xl:px-6 py-2.5 rounded-full font-medium transition-colors flex items-center space-x-2 text-sm shadow-sm hover:shadow-md"
            >
              <Calendar size={17} />
              <span>{t('nav.bookAppointment')}</span>
            </Link>
          </div>

          {/* Mobile Right Controls: Menu Button */}
          <div className="flex items-center space-x-2 lg:hidden">
            <button
              className="text-navy p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle Menu"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-gray-100 shadow-xl"
          >
            <div className="flex flex-col px-4 py-4 space-y-3">
              {links.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-base font-medium py-2 px-2 rounded-lg transition-colors ${
                    location.pathname === link.path 
                      ? 'text-clinical bg-clinical/10 font-semibold' 
                      : 'text-navy hover:bg-gray-50'
                  }`}
                >
                  {link.name}
                </Link>
              ))}

              <div className="pt-3 border-t border-gray-100">
                <Link
                  to="/appointment"
                  className="bg-clinical text-white px-4 py-3 rounded-xl font-medium text-center flex items-center justify-center space-x-2 shadow-sm"
                >
                  <Calendar size={18} />
                  <span>{t('nav.bookAppointment')}</span>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
